import React, { useEffect, useState } from 'react';
import { Trash2, ShoppingCart, TrendingUp, Info } from 'lucide-react';
import { SimulatorCartItem, TimePeriod, PERIOD_LABELS, PERIOD_DESCRIPTIONS, BalneotherapyType } from '../../types';
import { RevenueEvolution } from '../../../../utils/simulatorCalculations';
import { SimulatorSummary } from '../../types';
import SimulatorCartRow from './SimulatorCartRow';
import SimulatorCartMobileCard from './SimulatorCartMobileCard';
import PeriodSelector from '../PeriodSelector';
import './animations.css';

interface SimulatorCartTableProps {
  cartItems: SimulatorCartItem[];
  selectedPeriod: TimePeriod;
  itemEvolutions: Map<string, RevenueEvolution>;
  summary: SimulatorSummary;
  onUpdateQuantity: (itemId: string, newQuantity: number) => void;
  onUpdateHomeVisitPercentage: (itemId: string, percentage: number) => void;
  onUpdateBalneotherapy: (itemId: string, balneotherapy: BalneotherapyType) => void;
  onRemoveFromCart: (itemId: string) => void;
  onClearCart: () => void;
  onPeriodChange: (period: TimePeriod) => void;
  darkMode: boolean;
}

const SimulatorCartTable: React.FC<SimulatorCartTableProps> = ({
  cartItems,
  selectedPeriod,
  itemEvolutions,
  summary,
  onUpdateQuantity,
  onUpdateHomeVisitPercentage,
  onUpdateBalneotherapy,
  onRemoveFromCart,
  onClearCart,
  onPeriodChange,
  darkMode
}) => {
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [previousPeriod, setPreviousPeriod] = useState(selectedPeriod);

  useEffect(() => {
    if (previousPeriod !== selectedPeriod) {
      setIsTransitioning(true);
      setPreviousPeriod(selectedPeriod);
      const timer = setTimeout(() => { setIsTransitioning(false); }, 600);
      return () => clearTimeout(timer);
    }
  }, [selectedPeriod, previousPeriod]);

  if (cartItems.length === 0) {
    return (
      <div className={`text-center py-12 rounded-lg border-2 border-dashed ${
        darkMode ? 'border-appines-white/20 bg-appines-petrol' : 'border-[rgba(1,42,54,0.2)] bg-[rgba(1,42,54,0.04)]'
      }`}>
        <ShoppingCart className={`h-12 w-12 mx-auto mb-4 ${
          darkMode ? 'text-appines-white/25' : 'text-appines-petrol/25'
        }`} />
        <h3 className={`text-lg font-medium mb-2 ${
          darkMode ? 'text-appines-white' : 'text-appines-black'
        }`}>
          Aucun acte sélectionné
        </h3>
        <p className={darkMode ? 'text-appines-white/65' : 'text-appines-petrol/65'}>
          Recherchez et ajoutez des actes pour commencer la simulation
        </p>
      </div>
    );
  }

  return (
    <div className={`rounded-lg border ${
      darkMode ? 'bg-appines-petrol border-appines-white/12' : 'bg-appines-white border-[rgba(1,42,54,0.12)]'
    }`}>
      {/* Header */}
      <div className={`p-3 md:p-4 border-b space-y-3 md:space-y-4 ${darkMode ? 'border-appines-white/12' : 'border-[rgba(1,42,54,0.12)]'}`}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center min-w-0">
            <ShoppingCart className="h-5 w-5 mr-2 text-appines-petrol shrink-0" />
            <h3 className={`text-base md:text-lg font-semibold truncate ${
              darkMode ? 'text-appines-white' : 'text-appines-black'
            }`}>
              <span className="md:hidden">Panier ({cartItems.length})</span>
              <span className="hidden md:inline">Actes sélectionnés ({cartItems.length})</span>
            </h3>
          </div>
          <button
            onClick={onClearCart}
            aria-label="Vider le panier"
            title="Vider le panier"
            className={`flex items-center justify-center p-2 md:px-3 md:py-2 text-sm rounded-lg transition-colors ${
              darkMode ? 'text-appines-petal hover:bg-appines-petal/10' : 'text-[#c04040] hover:text-[#a03030] hover:bg-appines-petal/10'
            }`}
          >
            <Trash2 className="h-4 w-4 md:mr-1" />
            <span className="hidden md:inline">Vider le panier</span>
          </button>
        </div>

        {/* Period Selector */}
        <div className={`p-3 md:p-4 rounded-lg ${
          darkMode ? 'bg-appines-white/5' : 'bg-appines-glass/15'
        }`}>
          <PeriodSelector
            selectedPeriod={selectedPeriod}
            onPeriodChange={onPeriodChange}
            darkMode={darkMode}
            showLabel={true}
          />
        </div>
      </div>

      {/* Mobile: stacked cards */}
      <div className={`md:hidden p-3 space-y-3 ${isTransitioning ? 'period-transition' : ''}`}>
        {cartItems.map((item) => {
          const evolution = itemEvolutions.get(item.id);
          return (
            <SimulatorCartMobileCard
              key={item.id}
              item={item}
              evolution={evolution}
              selectedPeriod={selectedPeriod}
              onUpdateQuantity={onUpdateQuantity}
              onUpdateHomeVisitPercentage={onUpdateHomeVisitPercentage}
              onUpdateBalneotherapy={onUpdateBalneotherapy}
              onRemoveFromCart={onRemoveFromCart}
              darkMode={darkMode}
            />
          );
        })}
      </div>

      {/* Desktop: full table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className={darkMode ? 'bg-appines-white/5' : 'bg-[rgba(1,42,54,0.04)]'}>
            <tr>
              <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                darkMode ? 'text-appines-white/45' : 'text-appines-petrol/65'
              }`}>Acte</th>
              <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                darkMode ? 'text-appines-white/45' : 'text-appines-petrol/65'
              }`}>Quantité / {PERIOD_LABELS[selectedPeriod].toLowerCase()}</th>
              <th className={`px-4 py-3 text-center text-xs font-medium uppercase tracking-wider ${
                darkMode ? 'text-appines-white/45' : 'text-appines-petrol/65'
              }`}>% Domicile</th>
              <th className={`px-4 py-3 text-center text-xs font-medium uppercase tracking-wider ${
                darkMode ? 'text-appines-white/45' : 'text-appines-petrol/65'
              }`}>
                <div className="flex items-center justify-center gap-1">
                  <span>Balnéo</span>
                  <div className="group relative">
                    <Info className="h-3 w-3 cursor-help" />
                    <div className={`absolute right-0 top-full mt-2 w-72 p-3 rounded-lg shadow-lg z-10 hidden group-hover:block ${
                      darkMode ? 'bg-appines-petrol text-appines-white/65 border border-appines-white/12' : 'bg-appines-white text-appines-petrol/65 border border-[rgba(1,42,54,0.12)]'
                    }`}>
                      <p className="text-xs font-semibold mb-2">Avenant 7 - Revalorisation balnéothérapie</p>
                      <ul className="text-xs space-y-1">
                        <li>Piscine: 4,87 (2025) - 7,74 (2026+)</li>
                        <li>Bassin: 2,65 (2025) - 5,52 (2026+)</li>
                        <li className="mt-2 italic">Le supplément s'ajoute au tarif de base</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </th>
              <th className={`px-4 py-3 text-right text-xs font-medium uppercase tracking-wider ${
                darkMode ? 'text-appines-white/45' : 'text-appines-petrol/65'
              }`}>
                <div className="flex flex-col items-end">
                  <span>Original</span>
                  <span className={`text-xs font-normal normal-case ${darkMode ? 'text-appines-white/35' : 'text-appines-petrol/45'}`}>{PERIOD_DESCRIPTIONS[selectedPeriod]}</span>
                </div>
              </th>
              <th className={`px-4 py-3 text-right text-xs font-medium uppercase tracking-wider ${
                darkMode ? 'text-appines-white/45' : 'text-appines-petrol/65'
              }`}>
                <div className="flex flex-col items-end">
                  <span>Actuel</span>
                  <span className={`text-xs font-normal normal-case ${darkMode ? 'text-appines-white/35' : 'text-appines-petrol/45'}`}>{PERIOD_DESCRIPTIONS[selectedPeriod]}</span>
                </div>
              </th>
              <th className={`px-4 py-3 text-right text-xs font-medium uppercase tracking-wider ${
                darkMode ? 'text-appines-white/45' : 'text-appines-petrol/65'
              }`}>
                <div className="flex flex-col items-end">
                  <span>2026</span>
                  <span className={`text-xs font-normal normal-case ${darkMode ? 'text-appines-white/35' : 'text-appines-petrol/45'}`}>{PERIOD_DESCRIPTIONS[selectedPeriod]}</span>
                </div>
              </th>
              <th className={`px-4 py-3 text-right text-xs font-medium uppercase tracking-wider ${
                darkMode ? 'text-appines-white/45' : 'text-appines-petrol/65'
              }`}>
                <div className="flex flex-col items-end">
                  <span>2027</span>
                  <span className={`text-xs font-normal normal-case ${darkMode ? 'text-appines-white/35' : 'text-appines-petrol/45'}`}>{PERIOD_DESCRIPTIONS[selectedPeriod]}</span>
                </div>
              </th>
              <th className={`px-4 py-3 text-center text-xs font-medium uppercase tracking-wider ${
                darkMode ? 'text-appines-white/45' : 'text-appines-petrol/65'
              }`}>Actions</th>
            </tr>
          </thead>
          <tbody className={`divide-y ${darkMode ? 'divide-appines-white/12' : 'divide-[rgba(1,42,54,0.08)]'} ${isTransitioning ? 'period-transition' : ''}`}>
            {cartItems.map((item) => {
              const evolution = itemEvolutions.get(item.id);
              return (
                <SimulatorCartRow
                  key={item.id}
                  item={item}
                  evolution={evolution}
                  selectedPeriod={selectedPeriod}
                  onUpdateQuantity={onUpdateQuantity}
                  onUpdateHomeVisitPercentage={onUpdateHomeVisitPercentage}
                  onUpdateBalneotherapy={onUpdateBalneotherapy}
                  onRemoveFromCart={onRemoveFromCart}
                  darkMode={darkMode}
                />
              );
            })}
          </tbody>
          
          {/* Summary Footer */}
          <tfoot className={`border-t-2 ${darkMode ? 'bg-appines-white/5 border-appines-white/20' : 'bg-[rgba(1,42,54,0.04)] border-[rgba(1,42,54,0.2)]'}`}>
            <tr className="font-semibold">
              <td className={`px-4 py-4 ${darkMode ? 'text-appines-white' : 'text-appines-black'}`}>
                <div className="flex items-center">
                  <span>Total ({summary.itemCount} acte{summary.itemCount > 1 ? 's' : ''})</span>
                </div>
              </td>
              <td className={`px-4 py-4 text-center ${darkMode ? 'text-appines-white' : 'text-appines-black'}`}>
                <div className="text-center">
                  <div className="font-semibold">{summary.totalQuantityInSelectedPeriod}</div>
                  <div className={`text-xs ${darkMode ? 'text-appines-white/45' : 'text-appines-petrol/65'}`}>
                    actes / {PERIOD_LABELS[selectedPeriod].toLowerCase()}
                  </div>
                </div>
              </td>
              <td className={`px-4 py-4 text-center ${darkMode ? 'text-appines-white' : 'text-appines-black'}`}></td>
              <td className={`px-4 py-4 text-center ${darkMode ? 'text-appines-white' : 'text-appines-black'}`}></td>
              <td className={`px-4 py-4 text-right ${darkMode ? 'text-appines-white' : 'text-appines-black'}`}>
                <div className="font-semibold">{summary.totalOriginalRevenue.toFixed(2)}&euro;</div>
              </td>
              <td className={`px-4 py-4 text-right ${darkMode ? 'text-appines-white' : 'text-appines-black'}`}>
                <div className="font-semibold">{summary.totalCurrentRevenue.toFixed(2)}&euro;</div>
              </td>
              <td className={`px-4 py-4 text-right ${darkMode ? 'text-appines-white' : 'text-appines-black'}`}>
                <div className="font-semibold">{summary.totalFutureRevenueSept2026.toFixed(2)}&euro;</div>
                {summary.totalFutureRevenueSept2026 !== summary.totalCurrentRevenue && (
                  <div className={`text-xs flex items-center justify-end ${
                    summary.totalFutureRevenueSept2026 > summary.totalCurrentRevenue ? 'text-appines-emerald'
                      : summary.totalFutureRevenueSept2026 < summary.totalCurrentRevenue ? 'text-[#c04040]'
                      : darkMode ? 'text-appines-white/45' : 'text-appines-petrol/65'
                  }`}>
                    <span>{summary.totalDifferenceSept2026 > 0 ? '+' : ''}{summary.totalDifferenceSept2026.toFixed(2)}&euro; ({summary.totalPercentageChangeSept2026 > 0 ? '+' : ''}{summary.totalPercentageChangeSept2026.toFixed(1)}%)</span>
                    {Math.abs(summary.totalDifferenceSept2026) > 0.01 && (
                      <TrendingUp className={`h-3 w-3 ml-1 ${summary.totalDifferenceSept2026 > 0 ? 'text-appines-emerald' : 'text-[#c04040]'}`} />
                    )}
                  </div>
                )}
              </td>
              <td className={`px-4 py-4 text-right ${darkMode ? 'text-appines-white' : 'text-appines-black'}`}>
                <div className="font-semibold">{summary.totalFutureRevenue2027.toFixed(2)}&euro;</div>
                {Math.abs(summary.totalDifference2027) > 0.01 && (
                  <div className={`text-xs flex items-center justify-end ${
                    summary.totalDifference2027 > 0 ? 'text-appines-emerald'
                      : summary.totalDifference2027 < 0 ? 'text-[#c04040]'
                      : darkMode ? 'text-appines-white/45' : 'text-appines-petrol/65'
                  }`}>
                    <span>{summary.totalDifference2027 > 0 ? '+' : ''}{summary.totalDifference2027.toFixed(2)}&euro; ({summary.totalPercentageChange2027 > 0 ? '+' : ''}{summary.totalPercentageChange2027.toFixed(1)}%)</span>
                    <TrendingUp className={`h-3 w-3 ml-1 ${summary.totalDifference2027 > 0 ? 'text-appines-emerald' : 'text-[#c04040]'}`} />
                  </div>
                )}
              </td>
              <td className="px-4 py-4"></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default SimulatorCartTable;
